import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Crosshair,
  Layers,
  Map as MapIcon,
  Plus,
  LogIn,
  ChevronDown,
  User,
  Bookmark,
  LogOut,
  Sun,
  Moon,
  Shield,
  Activity,
  Radio,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore.js';
import { useThemeStore } from '../../store/themeStore.js';
import { useMapStore } from '../../store/mapStore.js';
import type { MapVisualMode } from '../map/MapView.js';

interface TopBarProps {
  visualMode: MapVisualMode;
  onVisualModeChange: (mode: MapVisualMode) => void;
}

function CoordinateDisplay() {
  const center = useMapStore((s) => s.center);
  const zoom = useMapStore((s) => s.zoom);
  const [time, setTime] = useState(() => new Date().toLocaleTimeString('en-US', { hour12: false }));

  useEffect(() => {
    const id = setInterval(() => {
      setTime(new Date().toLocaleTimeString('en-US', { hour12: false }));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const lat = center.lat.toFixed(4);
  const lng = center.lng.toFixed(4);

  return (
    <div
      className="mono"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        fontSize: 11,
        color: 'var(--text-muted)',
        letterSpacing: '0.06em',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 8px',
          background: 'rgba(88,166,255,0.06)',
          border: '1px solid rgba(88,166,255,0.15)',
          borderRadius: 3,
        }}
      >
        <Crosshair size={10} style={{ color: 'var(--accent)' }} />
        <span style={{ color: 'var(--text-primary)' }}>{lat}°N</span>
        <span style={{ color: 'var(--text-dim)' }}>/</span>
        <span style={{ color: 'var(--text-primary)' }}>{lng}°</span>
        <span style={{ color: 'var(--text-dim)', marginLeft: 4 }}>Z{Math.round(zoom)}</span>
      </span>
      <span style={{ color: 'var(--text-dim)' }}>{time}</span>
    </div>
  );
}

function UserSection() {
  const { user, logout } = useAuthStore();
  const { theme, toggle } = useThemeStore();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggle}
        aria-label="Toggle theme"
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 32,
          height: 32,
          border: '1px solid var(--border)',
          borderRadius: 4,
          background: 'transparent',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          transition: 'border-color 150ms, color 150ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
      >
        {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
      </button>

      {/* Add place */}
      <button
        type="button"
        onClick={() => navigate('/contribute')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 32,
          padding: '0 12px',
          border: '1px solid var(--border)',
          borderRadius: 4,
          background: 'transparent',
          color: 'var(--text-muted)',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'border-color 150ms, color 150ms',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
      >
        <Plus size={13} /> Add Place
      </button>

      {/* User menu */}
      {user ? (
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              height: 32,
              padding: '0 10px',
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: 'transparent',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              transition: 'border-color 150ms',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--accent-dim)',
                border: '1px solid rgba(88,166,255,0.3)',
                display: 'grid',
                placeItems: 'center',
                color: 'var(--accent)',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {user.name.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 500, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.name.split(' ')[0]}
            </span>
            <ChevronDown size={13} style={{ opacity: 0.5 }} />
          </button>

          {open && (
            <>
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 49 }}
                onClick={() => setOpen(false)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  zIndex: 50,
                  minWidth: 200,
                  background: 'var(--panel)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{user.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{user.email}</div>
                </div>
                {[
                  { icon: User, label: 'Profile', path: '/profile' },
                  { icon: Bookmark, label: 'Saved Places', path: '/profile' },
                  { icon: Activity, label: 'Activity', path: '/activity' },
                  ...(user.role === 'admin' ? [{ icon: Shield, label: 'Admin', path: '/admin' }] : []),
                ].map(({ icon: Icon, label, path }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => { navigate(path); setOpen(false); }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 14px',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 120ms, color 120ms',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(88,166,255,0.06)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                  >
                    <Icon size={14} /> {label}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    onClick={() => { logout(); navigate('/'); setOpen(false); }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 14px',
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 120ms, color 120ms',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--red-dim)'; e.currentTarget.style.color = 'var(--red)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/login')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            padding: '0 12px',
            border: '1px solid var(--accent)',
            borderRadius: 4,
            background: 'var(--accent-dim)',
            color: 'var(--accent)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <LogIn size={13} /> Sign In
        </button>
      )}
    </div>
  );
}

export default function TopBar({ visualMode, onVisualModeChange }: TopBarProps) {
  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 'var(--topbar-h)',
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        background: 'rgba(22, 27, 34, 0.98)',
        borderBottom: '1px solid var(--border)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Left: Wordmark */}
      <Link
        to="/"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 18px',
          height: '100%',
          borderRight: '1px solid var(--border)',
          textDecoration: 'none',
          flexShrink: 0,
        }}
      >
        <Radio size={16} style={{ color: 'var(--accent)' }} />
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--text-primary)',
          }}
        >
          WINGMAN
        </span>
        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.08em',
            color: 'var(--text-dim)',
            padding: '1px 5px',
            border: '1px solid var(--border)',
            borderRadius: 3,
          }}
          className="mono"
        >
          v1
        </span>
      </Link>

      {/* Center: Live coordinate readout */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
          padding: '0 16px',
        }}
      >
        <CoordinateDisplay />
      </div>

      {/* Right: Mode toggles + user */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          height: '100%',
          borderLeft: '1px solid var(--border)',
          paddingLeft: 12,
          paddingRight: 12,
          flexShrink: 0,
        }}
      >
        {/* Map mode selector */}
        <div
          style={{
            display: 'flex',
            border: '1px solid var(--border)',
            borderRadius: 4,
            overflow: 'hidden',
            marginRight: 12,
          }}
        >
          {([
            { mode: 'raw' as const, label: 'RAW', Icon: MapIcon },
            { mode: 'blueprint' as const, label: 'LAYERS', Icon: Layers },
          ] as const).map(({ mode, label, Icon }) => (
            <button
              key={mode}
              type="button"
              onClick={() => onVisualModeChange(mode)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                height: 30,
                padding: '0 10px',
                border: 'none',
                borderRight: mode === 'raw' ? '1px solid var(--border)' : 'none',
                background: visualMode === mode ? 'var(--accent-dim)' : 'transparent',
                color: visualMode === mode ? 'var(--accent)' : 'var(--text-muted)',
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.1em',
                cursor: 'pointer',
                transition: 'background 120ms, color 120ms',
              }}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        <UserSection />
      </div>
    </header>
  );
}
